import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import RangeSlider from 'react-bootstrap-range-slider';
import cx from 'classnames';
import AttributeProp from './propTypes/attributeProp';
import CategoryProp from './propTypes/categoryProp';
import translate from '../../commonComponents/translate';
import { displayMoney } from '../../services/utilityFunctions';
import SearchInput from '../../commonComponents/SearchInput';
import CustomCheckbox from '../../commonComponents/forms/inputs/CustomCheckbox';
import ArrowUp from '../../commonComponents/icons/ArrowUp';

function ShopMobileFilters(props) {
  const {
    t,
    query,
    catalogTypes,
    categories,
    attributes,
    priceRange,
    handleQueryChange,
    handleSearchChange,
  } = props;

  const FILTERS = {
    CATALOG_TYPE: 'catalogType',
    CATEGORIES: 'categories',
    ATTRIBUTES: 'attributes',
  };

  const [maxPrice, setPrice] = useState(query.maxPrice);
  const [openItems, setOpenItems] = useState([FILTERS.CATALOG_TYPE]);
  const [openAttributes, setOpenAttributes] = useState([]);
  const allCategories = [{ _id: '', name: t('all') }].concat(categories);

  const handlePriceChange = evt => {
    const value = Number(evt.target.value);
    setPrice(value);
  };
  const handleAfterPriceChange = () => handleQueryChange({ maxPrice });

  const handleChange = value => evt => {
    evt.preventDefault();
    handleQueryChange(value);
  };

  const toggleItem = filter => () =>
    setOpenItems(open =>
      open.includes(filter)
        ? open.filter(f => f !== filter)
        : open.concat(filter),
    );

  const toggleAttribute = filter => () =>
    setOpenAttributes(open =>
      open.includes(filter)
        ? open.filter(f => f !== filter)
        : open.concat(filter),
    );

  const handleAttributeChange = value => evt => {
    const isChecked = evt.target.checked;
    const attribute = query.attributes[value.attribute]
      ? [...query.attributes[value.attribute], value.value]
      : [value.value];
    const filtered = attribute.filter(
      v => (value.value === v && isChecked) || value.value !== v,
    );
    const attr = { ...query.attributes };
    attr[value.attribute] = filtered;
    for (const i in attr) {
      if (!attr[i].length) {
        delete attr[i];
      }
    }
    handleQueryChange({ attributes: attr });
  };

  useEffect(() => setPrice(query.maxPrice), [query.maxPrice]);

  return (
    <div className="shopMobileFilters mt-5 mb-3">
      <div className="shopMobileInputFilter mb-4">
        <SearchInput
          id="mobileSearchInput"
          onSearchChange={handleSearchChange}
          placeholder={t('search.products')}
          buttonText={t('search_button')}
          hasButton={false}
          value={query.search}
        />
      </div>
      <div className="filterBox">
        <span className="shopMobileFiltersTitle">
          {t('shop.product.filters')}
        </span>
        <div className="shopMobileFiltersGroup">
          <ul className="list-group">
            <li
              key={FILTERS.CATALOG_TYPE}
              className={cx(
                'list-group-item',
                'd-flex',
                'align-items-center',
                'mobile-shop-filters',
                { selected: openItems.includes(FILTERS.CATALOG_TYPE) },
              )}
              onClick={toggleItem(FILTERS.CATALOG_TYPE)}
            >
              <ArrowUp />
              {t('shop.product.catalog_type')}
            </li>
            <ul
              key={`${FILTERS.CATALOG_TYPE}-list`}
              className="sub-list"
              type="disc"
            >
              {catalogTypes.map(catalog => (
                <li key={catalog.id}>
                  <button
                    type="button"
                    className={cx({
                      selected: query.productType === catalog.catalogType,
                    })}
                    onClick={() =>
                      handleQueryChange({
                        productType: catalog.catalogType,
                        category: '',
                      })
                    }
                  >
                    {catalog.name}
                  </button>
                </li>
              ))}
            </ul>
            <li
              key={FILTERS.CATEGORIES}
              className={cx(
                'list-group-item',
                'd-flex',
                'align-items-center',
                'mobile-shop-filters',
                { selected: openItems.includes(FILTERS.CATEGORIES) },
              )}
              onClick={toggleItem(FILTERS.CATEGORIES)}
            >
              <ArrowUp />
              {t('shop.product.category')}
            </li>
            <ul
              key={`${FILTERS.CATEGORIES}-list`}
              className="sub-list"
              type="disc"
            >
              {allCategories.map(category => (
                <li key={`category-${category._id}`}>
                  <button
                    type="button"
                    className={cx({
                      selected: category._id === query.category,
                    })}
                    onClick={evt => {
                      handleChange({ category: category._id })(evt);
                    }}
                  >
                    {category._id === ''
                      ? category.name
                      : `${category.name} (${category.productsInCategory})`}
                  </button>
                </li>
              ))}
            </ul>
            <li
              key={FILTERS.ATTRIBUTES}
              className={cx(
                'list-group-item',
                'd-flex',
                'align-items-center',
                'mobile-shop-filters',
                { selected: openItems.includes(FILTERS.ATTRIBUTES) },
              )}
              onClick={toggleItem(FILTERS.ATTRIBUTES)}
            >
              <ArrowUp />
              {t('shop.product.attributes')}
            </li>
            <ul
              key={`${FILTERS.ATTRIBUTES}-list`}
              className="sub-list mobile-sub-list mobile-attribute-list"
              type="disc"
            >
              {attributes?.map(attribute =>
                !query.category ||
                attribute.category.includes(query.category) ? (
                  <div
                    className={cx('attributeItemGroup', {
                      selected: openAttributes.includes(attribute._id),
                    })}
                    key={`attributeMobile-${attribute._id}`}
                  >
                    <button
                      type="button"
                      className="attributeLabel"
                      onClick={toggleAttribute(attribute._id)}
                    >
                      <span className="attributeLabelText">
                        <ArrowUp />
                        {attribute.name}
                      </span>
                    </button>
                    <div className="attributeValuesList">
                      {attribute.values.map(value => (
                        <CustomCheckbox
                          id={`attributeMobile-${attribute._id}-${value}`}
                          onChange={handleAttributeChange({
                            attribute: attribute._id,
                            value,
                          })}
                          label={`${value}${attribute.unit}`}
                        />
                      ))}
                    </div>
                  </div>
                ) : null,
              )}
            </ul>
          </ul>
        </div>
        <div className="filterBoxPriceRange mt-2">
          <span className="filterBoxPriceRangeTitle">
            {t('shop.product.max_price')}
          </span>
          <RangeSlider
            value={maxPrice}
            max={priceRange.max}
            min={0}
            variant="primary"
            tooltip="off"
            onChange={handlePriceChange}
            onAfterChange={handleAfterPriceChange}
          />
          <div className="price-range-values">
            <span className="initial-value">{t('free')}</span>
            <span className="current-value">
              {query.maxPrice
                ? displayMoney(maxPrice, priceRange.symbol)
                : t('free')}
            </span>
            <span className="final-value">
              {displayMoney(priceRange.max, priceRange.symbol)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

ShopMobileFilters.propTypes = {
  t: PropTypes.func.isRequired,
  catalogTypes: PropTypes.arrayOf(
    PropTypes.shape({
      catalogType: PropTypes.string,
      total: PropTypes.number,
    }),
  ).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  attributes: PropTypes.arrayOf(PropTypes.shape(AttributeProp.propType))
    .isRequired,
  handleQueryChange: PropTypes.func.isRequired,
  handleSearchChange: PropTypes.func.isRequired,
  priceRange: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
    currency: PropTypes.string,
    symbol: PropTypes.string,
  }),
  query: PropTypes.shape({
    category: PropTypes.string.isRequired,
    maxPrice: PropTypes.number.isRequired,
    search: PropTypes.string.isRequired,
    attributes: PropTypes.shape({}).isRequired,
    productType: PropTypes.string,
  }).isRequired,
};

ShopMobileFilters.defaultProps = {
  priceRange: PropTypes.shape({
    min: 0,
    max: 10000,
    currency: 'EUR',
    symbol: '€',
  }),
};

export default translate(ShopMobileFilters);
